import type { DyspatchEvent, TaskSize } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { type ComponentProps, type ComponentType, type ReactNode, useId, useMemo } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { useViewPreference } from '@/hooks/use-view-preference';
import { type CollapsedEvent, collapseConsecutiveEvents } from '@/lib/collapse-events';
import {
  EVENT_KINDS,
  eventColor,
  eventDetail,
  isSubordinateKind,
  labelForKind,
} from '@/lib/event-kinds';
import { prState, SIZE_LABEL, sizeTitle } from '@/lib/fleet';
import {
  ago,
  clockTime,
  duration,
  minutesSince,
  type WaitLevel,
  waitLevel,
  waitThresholds,
} from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { cn } from '@/lib/utils';

/**
 * What a wait looks like at each of the two thresholds `lib/format.ts` sets.
 *
 * The rule is there because it is arithmetic; the colour is here because it is
 * presentation, and every surface that prints a wait -- the row, the strip, the
 * review page -- reads both from one place rather than picking its own.
 */
export const WAIT_TONE: Record<WaitLevel, string> = {
  fresh: 'text-fg-3',
  late: 'text-warn',
  stale: 'text-bad',
};

/**
 * An age, coloured once it is genuinely late, and only where waiting is the
 * point.
 *
 * The first threshold is four hours, not thirty minutes. At thirty minutes every
 * gate on a normal morning is amber, and a page where every number is a warning
 * has no warnings on it - the colour stops meaning "look at this" and starts
 * meaning "this is an age". Pass `staleAfterMinutes` where the hub's own number
 * is on hand; without it the compiled-in default says the same thing.
 */
export function Age({
  minutes,
  hot = false,
  staleAfterMinutes,
}: {
  minutes: number;
  hot?: boolean;
  staleAfterMinutes?: number | null;
}) {
  const level = hot ? waitLevel(minutes, waitThresholds(staleAfterMinutes)) : 'fresh';
  return <span className={cn('tnum text-2xs', WAIT_TONE[level])}>{duration(minutes)}</span>;
}

/**
 * Rows that are the right shape before they have any content.
 *
 * A list that says "Loading tasks…" tells you the page is working; a list of
 * grey rows tells you what is about to arrive and where, and it does not move
 * the layout when it does. Used on the three lists a person lands on first.
 */
/**
 * A button that refuses, and says why to everybody.
 *
 * The console's pattern for a verb the hub would reject is a disabled button
 * with the reason in its `title`, which reaches exactly one audience: a mouse
 * user who hovers. A `disabled` button is out of the tab order, so a keyboard
 * user cannot land on it to find out; a `title` is not announced reliably by
 * screen readers and is unreachable on a touch screen; and `disabled` sets
 * `pointer-events: none`, so on our own Button the tooltip never opens at all
 * and the wrapper spans that worked around that were only ever a mouse fix.
 *
 * So the button stays focusable and stays announced. `aria-disabled` tells
 * assistive technology it will not act, `aria-describedby` points at the
 * sentence, and the click handler is guarded because an aria-disabled button is
 * still a real button. `title` rides along for the pointer.
 *
 * `refusal` null means there is nothing to refuse and this is an ordinary
 * button.
 */
export function GuardedButton({
  refusal,
  className,
  onClick,
  children,
  ...props
}: ComponentProps<typeof Button> & { refusal: string | null }) {
  const described = useId();
  return (
    <>
      <Button
        {...props}
        aria-disabled={refusal ? true : undefined}
        aria-describedby={refusal ? described : undefined}
        title={refusal ?? props.title}
        className={cn('aria-disabled:cursor-not-allowed aria-disabled:opacity-45', className)}
        onClick={(event) => {
          if (refusal) {
            event.preventDefault();
            // Saying it, rather than only not acting. The reason used to live in
            // `title` and in the `sr-only` span below: a screen reader heard it,
            // a patient mouse hovered for it, and everybody else clicked a
            // button and watched nothing happen. "Retire isn't working" is what
            // that looks like from the outside, and it is indistinguishable from
            // a broken button.
            toast.info(refusal);
            return;
          }
          onClick?.(event);
        }}
      >
        {children}
      </Button>
      {refusal ? (
        <span id={described} className="sr-only">
          {refusal}
        </span>
      ) : null}
    </>
  );
}

export function RowSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <ul className="divide-y" aria-hidden>
      {Array.from({ length: rows }, (_, index) => index).map((index) => (
        <li key={index} className="flex items-center gap-2.5 px-gutter py-row">
          <span className="bg-raised size-[13px] shrink-0 animate-pulse rounded-full" />
          <span className="bg-raised h-[13px] w-10 shrink-0 animate-pulse rounded-sm" />
          <span
            className="bg-raised h-[13px] animate-pulse rounded-sm"
            style={{ width: `${38 - index * 7}%` }}
          />
          <span className="bg-raised ml-auto h-[13px] w-9 shrink-0 animate-pulse rounded-sm" />
        </li>
      ))}
    </ul>
  );
}

/**
 * The repo hue, unchanged: the same hash the server-rendered dashboard used, so a
 * repo is the same colour on every page a person has ever seen it on.
 *
 * What changed is how much of it is spent. A page with thirty coloured pills on
 * it is confetti, and confetti is what a monochrome page is trying not to be. So
 * the hue survives as a 5px dot and the label goes back to being text: the
 * recognition is still instant, and the page stays quiet.
 */
function repoHue(repo: string): string {
  let hash = 0;
  for (let index = 0; index < repo.length; index++) {
    hash = ((hash << 5) - hash + repo.charCodeAt(index)) | 0;
  }
  return `hsl(${Math.abs(hash) % 360}, 46%, 52%)`;
}

export function repoColor(repo: string): string {
  return repoHue(repo);
}

/** The repo's dot on its own, for a nav row or a table cell. */
export function RepoDot({ repo, className }: { repo: string; className?: string }) {
  return (
    <span
      className={cn('size-[6px] shrink-0 rounded-full', className)}
      style={{ background: repoHue(repo) }}
      aria-hidden
    />
  );
}

/**
 * The repo, as a quiet tinted chip. The tint is the repo's own hue at 10%, the
 * text is the hue mixed towards the foreground so it holds contrast on both
 * themes, and the dot is the same hue at full strength. Colour coding that a
 * person can read across a page, without the page becoming confetti.
 */
export function RepoChip({ repo, extra = 0 }: { repo: string; extra?: number }) {
  const hue = repoHue(repo);
  return (
    <span
      className="inline-flex h-[18px] min-w-0 max-w-full items-center gap-1.5 rounded-sm px-1.5 text-2xs font-medium"
      style={{
        background: `color-mix(in srgb, ${hue} 11%, transparent)`,
        color: `color-mix(in srgb, ${hue} 70%, var(--dy-fg))`,
      }}
    >
      <span className="size-[5px] shrink-0 rounded-full" style={{ background: hue }} aria-hidden />
      {/* The name truncates inside its own chip rather than being clipped by
          whatever is holding it: a cell with `overflow-hidden` cut
          "compliance-app-ai-team" off mid-word with no ellipsis, which reads
          as a different repo rather than as a shortened one. */}
      <span className="min-w-0 truncate" title={repo}>
        {repo}
      </span>
      {extra > 0 ? <span className="shrink-0 opacity-60">+{extra}</span> : null}
    </span>
  );
}

/**
 * A task's size, as a chip rather than a bare word (issue #64): `state`,
 * `repo` and the dependency list below all read as chips now, and a size
 * that stayed plain text was the one shape in the row a person could not
 * pick out at a glance.
 */
export function SizeChip({ size, className }: { size: TaskSize; className?: string }) {
  return (
    <Badge variant="secondary" className={className} title={sizeTitle(size)}>
      {SIZE_LABEL[size]}
    </Badge>
  );
}

/**
 * How to reach the command palette, in the words of the device you are holding.
 *
 * There is no cmd key on a phone, and below `lg` there is no rail either: the
 * palette is the search button in the view header, and that is what the sentence
 * names. (The empty peek that says this is itself `xl`-only today, so the second
 * half of it is a promise rather than a fix; the hint a phone does see is the
 * one on the rail's own palette button.)
 */
export function PaletteHint({ children }: { children: ReactNode }) {
  return (
    <>
      <span className="max-lg:hidden">
        Press <kbd className="dy-kbd">⌘K</kbd> to {children}
      </span>
      <span className="lg:hidden">Tap the search icon above to {children}</span>
    </>
  );
}

/** Where the work came in from: Slack, GitHub, the CLI, a schedule. */
export function SourceChip({ source }: { source?: string | null }) {
  if (!source) return null;
  return <span className="text-fg-3 shrink-0 text-2xs">{source}</span>;
}

/**
 * A task key. Monospaced and quiet, because it is an index, not a title.
 *
 * `briefId` qualifies it. Task keys are graph-local - every brief starts again
 * at T-1 - so a list that mixes briefs shows two `T-1`s with nothing to tell
 * them apart, and a person reading "requeue T-1" has to guess which one. Pass
 * the brief and the key reads `B-swh3 / T-1`. Inside a brief leave it off: the
 * prefix would be identical on every row and would only cost width.
 */
export function TaskKey({ children, briefId }: { children: ReactNode; briefId?: string }) {
  return (
    <span className="text-fg-3 shrink-0 font-mono text-2xs tabular-nums">
      {briefId ? <span className="opacity-65">{shortKey(briefId)} / </span> : null}
      {children}
    </span>
  );
}

/**
 * A task's dependency keys, each its own chip, in place of the sentence
 * "after T2, T3, T4, T7" the console used to draw (issue #64) - one shape a
 * person can pick out from the row rather than a clause they have to parse.
 * Exported here rather than kept local to one page, so the board card and the
 * peek panel (issue #100) can draw the same chip instead of inventing one.
 *
 * `resolve` looks a key up against whatever sibling list the caller already
 * has loaded: a key that resolves links straight to that task, and one that
 * doesn't (every list this reads from is capped) stays a plain chip rather
 * than becoming a link to nowhere.
 */
export function DependencyChips({
  keys,
  resolve,
  className,
}: {
  keys: readonly string[];
  resolve: (key: string) => { taskId: string; title?: string } | undefined;
  className?: string;
}) {
  if (!keys.length) return null;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {keys.map((key) => {
        const target = resolve(key);
        return (
          <Badge key={key} variant="secondary" className="font-mono" title={target?.title}>
            {target ? (
              <Link
                to="/tasks/$taskId"
                params={{ taskId: target.taskId }}
                className="hover:text-gate transition-colors duration-100"
                onClick={(event) => event.stopPropagation()}
              >
                {key}
              </Link>
            ) : (
              key
            )}
          </Badge>
        );
      })}
    </span>
  );
}

/** The plain empty line inside a list. */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="text-fg-3 px-gutter py-5 text-center text-sm">{children}</p>;
}

/**
 * A query that failed, said so, in the place the list would have been.
 *
 * Without it a list whose fetch 500'd drew its empty state: "no jobs yet" over a
 * hub that has hundreds, "you do not own anything yet" over somebody's whole
 * week. An empty list and a broken one look identical from `data ?? []`, and
 * only one of them is worth a person's time to act on. The detail pages have
 * always distinguished them; this is the same distinction for the lists.
 *
 * `retry` is react-query's own `refetch`, so the button costs one request and
 * needs no reload.
 */
export function LoadError({
  what,
  error,
  retry,
}: {
  /** What could not be loaded, lowercase, for "could not load <what>". */
  what: string;
  error?: unknown;
  retry?: () => void;
}) {
  const detail = error instanceof Error ? error.message : null;
  return (
    <div className="flex flex-col items-center gap-1.5 px-6 py-10 text-center">
      <p className="text-fg-2 text-sm">Could not load {what}</p>
      {detail ? <p className="text-fg-3 max-w-[40ch] text-xs">{detail}</p> : null}
      {retry ? (
        <div className="pt-1.5">
          <Button size="sm" variant="outline" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The empty state with something to say. Every list in the app has bespoke copy
 * for its empty case; the ones a person may land on first get an explanation and
 * a way out of it, because a blank panel teaches nothing.
 */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-6 py-10 text-center">
      {Icon ? <Icon className="text-fg-3 mb-0.5 size-4 opacity-70" /> : null}
      <p className="text-fg-2 text-sm">{title}</p>
      {hint ? <p className="text-fg-3 max-w-[34ch] text-xs">{hint}</p> : null}
      {action ? <div className="pt-1.5">{action}</div> : null}
    </div>
  );
}

/** A group label. 10.5px wide-tracked caps; the quietest heading in the system. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('dy-eyebrow', className)}>{children}</div>;
}

/** " #412", or nothing. Callers concatenate: `Open PR${prNumber(url)}`. */
export function prNumber(url: string | null | undefined): string {
  const match = String(url ?? '').match(/\/pull\/(\d+)/);
  return match ? ` #${match[1]}` : '';
}

/**
 * The PR a step has produced, or the branch it hasn't produced one from yet -
 * the graph and the task pane each drew their own copy of this, and the plan
 * row and the steps list drew neither, so a held step with a draft PR out read
 * as `held` and nothing else (issue #29). One component now, so the three
 * facts `prState` distinguishes cannot read differently on two surfaces.
 *
 * `linked` defaults to false because two of the four callers draw this inside
 * a row that is already a `<Link>` or a `<button>`: a nested `<a>` is invalid
 * HTML, and the outer press would swallow the click before it reached the
 * inner one anyway. Those rows print the same words as plain text and leave
 * opening the PR to the sheet the row itself opens.
 */
export function PrMark({
  prUrl,
  prDraft,
  branch,
  linked = false,
  className,
}: {
  prUrl?: string | null;
  prDraft?: boolean | null;
  branch?: string | null;
  linked?: boolean;
  className?: string;
}) {
  const state = prState({ prUrl, prDraft, branch });
  if (!state) return null;
  if (state === 'pushed') {
    return (
      <span
        className={cn('text-fg-3 shrink-0 text-2xs', className)}
        title={`Branch pushed, no pull request yet: ${branch}`}
      >
        pushed, no PR
      </span>
    );
  }
  const label = `${state === 'draft' ? 'draft ' : ''}PR${prNumber(prUrl)}`;
  if (!linked) {
    return (
      <span
        className={cn('text-fg-3 shrink-0 text-2xs', className)}
        title={
          state === 'draft'
            ? 'A draft pull request is open. Open it from the task.'
            : 'The pull request is ready for review. Open it from the task.'
        }
      >
        {label}
      </span>
    );
  }
  return (
    <a
      href={prUrl ?? undefined}
      target="_blank"
      rel="noreferrer"
      className={cn('shrink-0 text-2xs underline', className)}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </a>
  );
}

/**
 * What a failed step actually printed, folded away.
 *
 * The reason is above it and says what failed and how to fix it; this is the
 * evidence, for the person who does not believe the reason or needs the session
 * id out of it. Closed at rest and monospaced when open, because it is output
 * and not prose, and scrolled rather than allowed to push the page down: a
 * result object is forty lines and this pane is 400px wide.
 */
export function FailureDetail({ detail, className }: { detail: string; className?: string }) {
  return (
    <details className={cn('text-2xs', className)}>
      <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
        What the task printed →
      </summary>
      <pre className="bg-sunken/60 text-fg-3 mt-1 max-h-64 overflow-auto rounded-sm p-2 font-mono whitespace-pre-wrap">
        {detail}
      </pre>
    </details>
  );
}

/**
 * The plain-language table, open from the log itself rather than left to
 * `docs/ARCHITECTURE.md`. Closed at rest, because a reader who already knows
 * the vocabulary should not have to scroll past it to reach the first event.
 *
 * Listed in `EVENT_KINDS` order, which is family order (a job's events, then a
 * task's, then an agent's...): a reader who opens this because one line
 * confused them is scanning for that one word, not reading top to bottom, and
 * family order at least keeps kin together while they do.
 */
export function EventKindLegend() {
  return (
    <details className="text-2xs">
      <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
        What these mean →
      </summary>
      <ul className="bg-sunken/60 text-fg-3 mt-1 max-h-64 overflow-auto rounded-sm p-2 font-mono">
        {EVENT_KINDS.map((kind) => (
          <li key={kind} className="flex items-baseline justify-between gap-3 py-0.5">
            <span className="text-fg-2">{labelForKind(kind)}</span>
            <span className="shrink-0">{kind}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The audit journal.
 *
 * The kind is printed the way every other surface prints it (`labelForKind`),
 * and the raw name the hub emitted is on the line's `title`: this is the record,
 * so the exact string has to stay reachable, but a column of `task.offer_expired`
 * is a namespace repeated forty times where the reader wanted the verb.
 *
 * A subordinate kind (`isSubordinateKind`) -- a task offered right before it is
 * claimed, an agent reporting in right before its PR reaches review -- prints
 * quieter than an ordinary line rather than getting merged into one: the two
 * events are one action wearing two beats, and the record stays exactly as long
 * as the journal actually is, but the reader's eye should land on the beat that
 * matters.
 *
 * A failure line is the message and nothing else, with whatever the step printed
 * under it as a closed disclosure. The journal is read by scanning, and a line
 * that unfolds forty lines of JSON into the middle of the scan is not a line.
 */
/** One row of the journal, shared by the full log and the collapsed summary
    line (`EventLogSection`) so the two read as the same record rather than
    two slightly different renderings of it. */
function EventRow({ event }: { event: CollapsedEvent }) {
  const detail = eventDetail(event.data);
  const quiet = isSubordinateKind(event.kind);
  return (
    <li
      className={cn(
        'flex items-start gap-2.5 px-gutter py-row text-xs',
        quiet ? 'text-fg-3' : 'text-fg-2',
      )}
      title={event.kind}
    >
      <time className="text-fg-3 tnum mt-[3px] shrink-0 font-mono text-2xs">
        {clockTime(event.createdAt)}
      </time>
      <span
        className="mt-[6px] size-[5px] shrink-0 rounded-full"
        style={{ background: eventColor(event.kind) }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="break-words">
          <span className={cn('font-mono text-2xs', quiet ? 'text-fg-3' : 'text-foreground')}>
            {labelForKind(event.kind)}
          </span>{' '}
          {event.message}
          {event.count > 1 ? (
            <span
              className="text-fg-3 ml-1.5 font-mono text-2xs"
              title={`Last seen ${new Date(event.lastSeenAt).toLocaleString()}`}
            >
              ×{event.count} · last seen {ago(minutesSince(event.lastSeenAt))}
            </span>
          ) : null}
        </p>
        {detail ? <FailureDetail detail={detail} className="mt-1" /> : null}
      </div>
    </li>
  );
}

export function EventLog({
  events,
  empty = 'No events yet.',
}: {
  events: DyspatchEvent[];
  empty?: string;
}) {
  // Consecutive, otherwise-identical rows read as one with a count (issue
  // #91) - a restart used to re-log an unchanged hold once per queued task,
  // and the rows that burst wrote stay in the journal exactly as written.
  // This is the only place that changes how they read.
  const rows = useMemo(() => collapseConsecutiveEvents(events), [events]);

  // A section inside a detail pane, not a page: its empty line is a line, not a
  // 96px hole between two headings.
  if (!rows.length) {
    return <p className="text-fg-3 px-gutter py-2.5 text-xs">{empty}</p>;
  }
  return (
    <div>
      <ul className="divide-y">
        {rows.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </ul>
      <div className="px-gutter py-1.5">
        <EventKindLegend />
      </div>
    </div>
  );
}

/**
 * The Events region on a job or task page: one row by default rather than the
 * whole journal, because the full log is the tallest thing on either page and
 * the least useful for most visits (issue #90). The heading names the region,
 * its count is the meta beside it, and the single row underneath is the most
 * recent line - the three things a collapsed view has to carry, per the
 * acceptance criteria, without a fourth boundary to draw them in.
 *
 * `scope` remembers the expanded/collapsed choice per viewer through
 * `useViewPreference` (issue #98), the same hook the jobs board's own view
 * choice already uses, rather than a fourth storage scheme next to it and
 * `useCollapsedGroups` (issue #68). A job page and a task page keep separate
 * scopes - expanding one says nothing about what a viewer wants from the
 * other.
 */
export function EventLogSection({
  events,
  empty,
  scope,
}: {
  events: DyspatchEvent[];
  empty: string;
  scope: string;
}) {
  const rows = useMemo(() => collapseConsecutiveEvents(events), [events]);
  const preference = useViewPreference(scope, false);
  const expanded = preference.value;
  const latest = rows[0];

  return (
    <Section>
      <SectionHead
        title="Events"
        meta={rows.length ? `${rows.length} on the record` : undefined}
        actions={
          latest ? (
            <Button variant="ghost" size="xs" onClick={() => preference.set(!expanded)}>
              {expanded ? 'Collapse' : 'Expand'}
            </Button>
          ) : undefined
        }
      />
      <Surface>
        {!latest ? (
          <p className="text-fg-3 px-gutter py-2.5 text-xs">{empty}</p>
        ) : expanded ? (
          <EventLog events={events} empty={empty} />
        ) : (
          <ul className="divide-y">
            <EventRow event={latest} />
          </ul>
        )}
      </Surface>
    </Section>
  );
}

/**
 * A label/value grid. `display: contents` on the row wrapper is what keeps the
 * two columns aligned across rows; a real wrapper element breaks it.
 */
export function MetaGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-gutter py-2.5 text-xs">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-fg-3">{row.label}</dt>
          <dd className="text-fg-2 min-w-0 break-words">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The properties of one record, as a band of icon, label and value.
 *
 * `MetaGrid` above is a label column and a value column, one fact per line,
 * running down the page. That is the honest shape for the handful of facts a
 * 400px pane can hold, and the wrong one for the sixteen a task carries on a
 * page three times that wide: the facts become a narrow ribbon of text with
 * most of the window empty beside them, and the reader scrolls past a screen
 * of nothing to reach the region underneath.
 *
 * So this is the shape the trackers settled on. The properties are a band
 * across the top of the record rather than a column beside it, two to a line
 * where there is room for two, and each one opens with an icon so a reader
 * hunting for the branch finds it by shape before reading a single label. The
 * icon is decorative in the strict sense, since the label beside it says the
 * same thing in words, so it is hidden from assistive technology and a row
 * without one still lines its label up with the rest through a spacer of the
 * same width.
 *
 * `columns` is here because a breakpoint cannot answer this on its own: `sm:`
 * asks how wide the window is, and the peek panel is 400px of a window that
 * may be 1600px. A caller that knows it is narrow has to be able to say so.
 */
export function PropertyGrid({
  rows,
  columns = 2,
}: {
  rows: { label: string; value: ReactNode; icon?: ComponentType<{ className?: string }> }[];
  columns?: 1 | 2;
}) {
  return (
    <dl
      className={cn(
        'grid grid-cols-1 gap-x-6 gap-y-2 px-gutter py-2.5 text-xs',
        columns === 2 && 'sm:grid-cols-2',
      )}
    >
      {rows.map((row) => {
        const Icon = row.icon;
        return (
          // A wrapper element per row rather than `display: contents` and one
          // shared track: each row here is its own label/value pair, and the
          // pairs are what the grid lays side by side.
          <div key={row.label} className="flex min-w-0 items-start gap-2">
            <dt className="text-fg-3 flex w-24 shrink-0 items-start gap-1.5">
              {Icon ? (
                <Icon className="text-fg-3 mt-px size-3.5 shrink-0" aria-hidden />
              ) : (
                <span className="mt-px size-3.5 shrink-0" aria-hidden />
              )}
              <span className="min-w-0">{row.label}</span>
            </dt>
            <dd className="text-fg-2 min-w-0 flex-1 break-words">{row.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}
